/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 *
 * Singleton for the google maps api. Used to ensure that the google maps api is loaded once when needed.
 * Also, the instance of google maps should be the same in order to avoid the creation of multiple different maps
 * in the application or unnecessary reloads.
 */
import {
  selectGpsSaNormalAccuracyMapLayers,
  selectGpsSaReducedAccuracyMapLayers,
  selectGpsSaLowAccuracyMapLayers,
  selectGpsSaMapLayersNormalAccuracyChecked,
  selectGpsSaMapLayersReducedAccuracyChecked,
  selectGpsSaMapLayersLowAccuracyChecked,
  selectGpsSaMapLayersRequestFinished,
  selectGpsSaMapLayersRequestError,
  selectGpsSaMapLayersChecked,
} from '../../../store';
import { useSelectorWithMapId } from './BaseMapContainer';
import { H3HexagonLayer } from '@deck.gl/geo-layers';
import { GpsSaMapLayer } from '../../../shared/entities/gpsSaMapLayer';
import { shallowEqual, useSelector } from 'react-redux';
import appTheme from '../../common/theme/appTheme.json';
import { rgbaFromHex } from '../../deprecated/GeoJSONMapOverlay';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Unit } from '@deck.gl/core';
import { cellToLatLng, CoordPair } from 'h3-js';

export const DEFAULT_COLOR_OPACITY = 102; // 40% opacity
export const HIGHLIGHT_COLOR_OPACITY = 204; // 80% opacity

const MAP_ZOOM_LEVEL_THRESHOLD = 6; // Threshold to determine if we should show hexagon outlines

type PositionAccuracyLayerOutput = {
  selectedHexagon: GpsSaMapLayer | null;
  gpsPositionAccuracyLayers: H3HexagonLayer<GpsSaMapLayer>[];
  selectedHexagonCentroid: google.maps.LatLng | undefined;
};

export const useGpsSaPositionAccuracyLayers = (map: google.maps.Map): PositionAccuracyLayerOutput => {
  const gpsSaEnabled = useSelectorWithMapId(selectGpsSaMapLayersChecked);
  const mapLayerRequestFinished = useSelector(selectGpsSaMapLayersRequestFinished, shallowEqual);
  const requestMapLayersError = useSelector(selectGpsSaMapLayersRequestError, shallowEqual);
  const normalAccuracyChecked = useSelectorWithMapId(selectGpsSaMapLayersNormalAccuracyChecked);
  const reducedAccuracyChecked = useSelectorWithMapId(selectGpsSaMapLayersReducedAccuracyChecked);
  const lowAccuracyChecked = useSelectorWithMapId(selectGpsSaMapLayersLowAccuracyChecked);
  const normalAccuracyMapLayers = useSelector(selectGpsSaNormalAccuracyMapLayers, shallowEqual);
  const reducedAccuracyMapLayers = useSelector(selectGpsSaReducedAccuracyMapLayers, shallowEqual);
  const lowAccuracyMapLayers = useSelector(selectGpsSaLowAccuracyMapLayers, shallowEqual);

  // State to track the selected hexagon
  const [selectedHexagon, setSelectedHexagon] = useState<GpsSaMapLayer | null>(null);

  // State to track the zoom level of the map
  const [zoomLevel, setZoomLevel] = useState<number | undefined>(undefined);

  // Ref to track if hover functionality is enabled
  // Note: Used a ref since the value needed to be updated without having to re-render the Deck.gl layers, otherwise handleHover would use a stale value in some cases
  const hoverEnabledRef = useRef(true);

  // Keep track of zoom level on map zoom changes
  useEffect(() => {
    if (map) {
      const listener = map.addListener('zoom_changed', () => {
        const currentZoom = map.getZoom();
        setZoomLevel(currentZoom);
      });

      return () => listener.remove();
    }

    // Return a no-op cleanup function if `map` is not defined
    return () => {};
  }, [map]);

  // Reset selected hexagon when GPS SA is disabled or map layers request returns with an error
  useEffect(() => {
    if (requestMapLayersError || !gpsSaEnabled) {
      // Reset states if there is an error or GPS SA is disabled
      hoverEnabledRef.current = true; // Re-enable hover
      setSelectedHexagon(null); // Reset selected hexagon
    }
  }, [requestMapLayersError, gpsSaEnabled]);

  // Save the hexagon ID of the hovered hexagon
  const handleHover = useCallback((info: any) => {
    // If hover functionality is disabled, do nothing
    if (!hoverEnabledRef.current) {
      return;
    }

    if (info.object) {
      setSelectedHexagon(info.object); // Set the hovered hexagon ID
    } else {
      setSelectedHexagon(null); // Reset if no hexagon is hovered
    }
  }, []);

  // Save the hexagon ID of the clicked hexagon
  const handleOnClick = useCallback(
    (info: any) => {
      if (info.object.h3Cell === selectedHexagon?.h3Cell && !hoverEnabledRef.current) {
        setSelectedHexagon(null); // Reset the selected hexagon
        hoverEnabledRef.current = true; // Re-enable hover if the same hexagon is clicked again
      } else {
        setSelectedHexagon(info.object); // Set the clicked hexagon ID
        hoverEnabledRef.current = false; // Disable hover functionality after click
      }
    },
    [selectedHexagon],
  );

  const selectedHexagonCentroid = useMemo(() => {
    if (!selectedHexagon?.h3Cell) return;

    const centroidLatLng = cellToLatLng(selectedHexagon.h3Cell) as CoordPair;

    return new google.maps.LatLng(centroidLatLng[0], centroidLatLng[1]);
  }, [selectedHexagon?.h3Cell]);

  // Memoize the common hexagon configuration to avoid unnecessary recalculations
  const commonH3HexagonConfig = useCallback(
    (hexColor: string) => {
      return {
        filled: true,
        stroked: zoomLevel && zoomLevel >= MAP_ZOOM_LEVEL_THRESHOLD ? true : false, // Only show outlines if zoom level is gte the threshold
        pickable: true,
        lineWidthUnits: 'pixels' as Unit,
        getLineWidth: 0.5,
        extruded: false,
        onHover: handleHover, // Handle hover event
        onClick: handleOnClick, // Handle click event
        getHexagon: (d: GpsSaMapLayer) => d.h3Cell,
        getFillColor: (d: GpsSaMapLayer) =>
          rgbaFromHex(hexColor, d.h3Cell === selectedHexagon?.h3Cell ? HIGHLIGHT_COLOR_OPACITY : DEFAULT_COLOR_OPACITY),
        getLineColor: rgbaFromHex(hexColor, DEFAULT_COLOR_OPACITY),

        // Update specific style properties when certain triggers are activated
        updateTriggers: {
          stroked: zoomLevel,
          getFillColor: selectedHexagon,
        },
      };
    },
    [zoomLevel, selectedHexagon, handleHover, handleOnClick],
  );

  const gpsPositionAccuracyLayers: H3HexagonLayer[] = useMemo(() => {
    // If the map layers request is not finished or there was an error, return an empty array
    if (!mapLayerRequestFinished || requestMapLayersError) {
      return [];
    }

    return [
      new H3HexagonLayer<GpsSaMapLayer>({
        id: 'GpsSaNormalAccuracy',
        data: normalAccuracyMapLayers,
        visible: normalAccuracyChecked,
        ...commonH3HexagonConfig(appTheme.normalAccuracyGreen), // Use the common config with normal accuracy color
      }),
      new H3HexagonLayer<GpsSaMapLayer>({
        id: 'GpsSaReducedAccuracy',
        data: reducedAccuracyMapLayers,
        visible: reducedAccuracyChecked,
        ...commonH3HexagonConfig(appTheme.reducedAccuracyYellow), // Use the common config with reduced accuracy color
      }),
      new H3HexagonLayer<GpsSaMapLayer>({
        id: 'GpsSaLowAccuracy',
        data: lowAccuracyMapLayers,
        visible: lowAccuracyChecked,
        ...commonH3HexagonConfig(appTheme.lowAccuracyRed), // Use the common config with low accuracy color
      }),
    ];
  }, [
    normalAccuracyChecked,
    reducedAccuracyChecked,
    lowAccuracyChecked,
    normalAccuracyMapLayers,
    reducedAccuracyMapLayers,
    lowAccuracyMapLayers,
    commonH3HexagonConfig,
    mapLayerRequestFinished,
    requestMapLayersError,
  ]);

  return { selectedHexagon, gpsPositionAccuracyLayers, selectedHexagonCentroid };
};
